// ════════════════════════════════════════════════════════════════════════════════
//  WorkOrders.js — Fleet work orders on real tables + unit work history     #FLEET_WO_TABLES_V1 (2026-10-14)
//
//  Bill (10/14 WO brief): "The field never touches a WO — they fix and cut a PO. A DVIR defect is the real
//  WO trigger and lands in Bob's queue. All POs and work orders stack inside the unit's work history."
//
//  WHERE IT LIVES   public.fleet_work_orders  (one row per WO)   public.fleet_wo_events (one row per log line)
//                   Was the app_storage blob fleet-work-orders { v, orders[] } (empty when this shipped).
//                   Every screen still works with the SAME order object it always did (no, unit, uk, status,
//                   assignee, shop, thirdParty, log[] …) — toRow / fromRow translate at the door, and fields
//                   with no column of their own (type, cat, ref, startedAt, log …) ride in the extra jsonb.
//
//  FORK             who owns the next move:  bob    — Needs Bob (DVIR defects, anything not routed yet)
//                                            shop   — the shop; unassigned = the Shop Pool anyone can grab
//                                            vendor — a 3rd-party shop; Bob closes it
//                                            field  — ops fixes it in the field; the PO closes it (Bob approves)
//
//  SAVES            woMutate re-reads every row, runs the change, then writes ONLY the rows that changed (upsert
//                   by id). A failed read writes nothing. Each new log line also lands in fleet_wo_events, and the
//                   app_storage key fleet-wo-stamp is touched so every open screen (useLiveKeys) refreshes.
// ════════════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { unitKey } from "./unitValid";
import { useLiveKeys } from "./liveKeys";
import { printKitReport } from "./pmKitReport";   // #PM_KIT_REPORT_V1

export const WO_TABLE = 'fleet_work_orders';
export const WO_STAMP_KEY = 'fleet-wo-stamp';      // touched on every WO save — the live-refresh signal
export const PO_LOG_CODES = ['bm', 'cj', 'ct', 'pg', 'rm', 'gw', 'jl', 'jk', 'kc', 'cv', 'nv', 'ap'];
export const FIELD_PO_START = '2026-10-14';        // field POs before go-live are history, not Bob's queue
export const WO_FORKS = { bob: 'Needs Bob', shop: 'Shop', vendor: 'Vendor', field: 'Field fix' };

// ── connection (App.js calls woConnect once with SUPA_URL / SUPA_KEY / _accessToken) ──────────────
let _conn = { supaUrl: '', supaKey: '', getToken: null };
export const woConnect = (c) => { if (c && c.supaUrl) _conn = { supaUrl: c.supaUrl, supaKey: c.supaKey, getToken: c.getToken || null }; };
const _tok = async (force, failed) => { try { return _conn.getToken ? await _conn.getToken(force, failed) : null; } catch { return null; } };
const rest = async (path, opts) => {
  const o = opts || {};
  if (!_conn.supaUrl) throw new Error('Work orders are not connected yet — reload the page.');
  const go = (tok) => fetch(`${_conn.supaUrl}/rest/v1/${path}`, { method: o.method || 'GET', body: o.body,
    headers: { apikey: _conn.supaKey, Authorization: `Bearer ${tok || _conn.supaKey}`, 'Content-Type': 'application/json', ...(o.headers || {}) } });
  const t0 = await _tok();
  if (!t0) throw new Error('Not signed in — sign in again, then retry.');
  let r = await go(t0);
  if (r.status === 401) { const t1 = await _tok(true, t0); if (t1) r = await go(t1); }
  if (!r.ok) { let t = ''; try { t = await r.text(); } catch { t = ''; } const e = new Error(`Work orders (${r.status}): ${t.slice(0, 180)}`); e.status = r.status; e.body = t; throw e; }
  const txt = await r.text();
  return txt ? JSON.parse(txt) : null;
};

// ── ids / helpers ───────────────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const woNewId = () => {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch { /* fall through */ }
  const h = () => Math.floor(Math.random() * 16).toString(16);
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => c === 'x' ? h() : ((Math.random() * 4 | 0) + 8).toString(16));
};
const isoDay = (v) => { const s = String(v || '').slice(0, 10); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null; };
const numOrNull = (v) => { if (v === '' || v == null) return null; const n = Number(v); return isFinite(n) ? n : null; };
const lc = (s) => String(s || '').trim().toLowerCase();
export const woLive = (o) => o && o.status !== 'done' && o.status !== 'void' && o.status !== 'moved';

// The fork a WO is really in, from what the screen changed. A mechanic taking a Needs-Bob / field item moves it
// to the shop; a vendor item taken back into the shop is shop again.
export const woFork = (o) => {
  if (o.thirdParty) return 'vendor';
  let f = o.fork || '';
  if (f === 'vendor') f = 'shop';
  if (o.assignee && (f === 'bob' || f === 'field')) f = 'shop';
  return f || (o.source === 'DVIR' && !o.assignee ? 'bob' : 'shop');
};

// ── row ↔ order ─────────────────────────────────────────────────────────────────
const MAPPED = new Set(['id', 'no', 'unit', 'uk', 'desc', 'source', 'fork', 'status', 'priority', 'complaint', 'defect', 'assignee', 'assigneeName',
  'shop', 'thirdParty', 'due', 'poNos', 'reading', 'workDone', 'hours', 'pmService', 'moveJob', 'moveReason', 'openedBy', 'openedByName', 'openedAt',
  'doneBy', 'doneByName', 'doneAt', 'updatedAt']);
export const woToRow = (o) => {
  const extra = {};
  Object.keys(o).forEach(k => { if (!MAPPED.has(k) && o[k] !== undefined) extra[k] = o[k]; });
  const fork = woFork(o);
  return {
    id: o.id, no: o.no, unit: o.unit || '', unit_key: o.uk || unitKey(o.unit), unit_desc: o.desc || '', source: o.source || 'Shop', fork,
    status: o.status || 'open', priority: o.type === 'Emergency' ? 'emergency' : (o.priority || 'normal'), complaint: o.complaint || '',
    dvir_id: (o.dvir && o.dvir.id) || null, dvir_date: isoDay(o.dvir && o.dvir.date), defect: o.defect || null,
    assignee: lc(o.assignee) || null, assignee_name: o.assigneeName || null, vendor: fork === 'vendor' ? (o.shop || null) : null, due: isoDay(o.due),
    po_nos: Array.isArray(o.poNos) ? o.poNos.map(String) : [], reading: numOrNull(o.reading), work_done: o.workDone || null, labor_hrs: numOrNull(o.hours),
    pm_service: !!o.pmService, move_job: o.moveJob || null, move_reason: o.moveReason || null,
    opened_by: o.openedBy || null, opened_by_name: o.openedByName || null, opened_at: o.openedAt || new Date().toISOString(),
    closed_by: o.doneBy || null, closed_by_name: o.doneByName || null, closed_at: o.doneAt || null, updated_at: new Date().toISOString(), extra,
  };
};
export const woFromRow = (r) => {
  const x = (r.extra && typeof r.extra === 'object') ? r.extra : {};
  return {
    ...x, id: r.id, no: r.no, unit: r.unit, uk: r.unit_key, desc: r.unit_desc || '', source: r.source || 'Shop', fork: r.fork || 'shop',
    status: r.status || 'open', priority: r.priority || 'normal', complaint: r.complaint || '', ...(r.defect ? { defect: r.defect } : {}),
    dvir: x.dvir || (r.dvir_id ? { id: r.dvir_id, date: r.dvir_date } : null),
    assignee: r.assignee || '', assigneeName: r.assignee_name || '', shop: r.vendor || '', thirdParty: r.fork === 'vendor', due: r.due || '',
    poNos: Array.isArray(r.po_nos) ? r.po_nos : [], reading: r.reading, workDone: r.work_done || '', hours: r.labor_hrs, pmService: !!r.pm_service,
    moveJob: r.move_job || '', moveReason: r.move_reason || '', openedBy: r.opened_by || '', openedByName: r.opened_by_name || '', openedAt: r.opened_at,
    doneBy: r.closed_by || '', doneByName: r.closed_by_name || '', doneAt: r.closed_at || null, updatedAt: r.updated_at,
  };
};

// ── read / write ────────────────────────────────────────────────────────────────
export const woLoadOrders = async (unitK) => {
  const q = `${WO_TABLE}?select=*${unitK ? `&unit_key=eq.${encodeURIComponent(unitK)}` : ''}&order=opened_at.asc&limit=20000`;
  const rows = await rest(q);
  return Array.isArray(rows) ? rows.map(woFromRow) : [];
};
// Never throws — screens that only show work orders get [] on a blip (and say so via .failed).
export const woLoadSafe = async (unitK) => { try { return await woLoadOrders(unitK); } catch { const a = []; a.failed = true; return a; } };

const touchStamp = async () => {
  try {
    await rest('app_storage', { method: 'POST', headers: { Prefer: 'resolution=merge-duplicates,return=minimal' },
      body: JSON.stringify({ key: WO_STAMP_KEY, value: JSON.stringify({ at: new Date().toISOString() }), updated_at: new Date().toISOString() }) });
  } catch { /* the other screens pick it up on their next load */ }
};
const eventKind = (o, p) => {
  if (!p) return 'opened';
  if (o.status !== p.status) return o.status;
  if (lc(o.assignee) !== lc(p.assignee)) return 'assigned';
  if (woFork(o) !== woFork(p)) return 'routed';
  return 'note';
};

// Re-read → change → write the changed rows. fn gets a copy of the full list and returns the new list (or null for
// "nothing to do"). One retry when two people grabbed the same WO number at once (unique no).
export const woMutate = async (fn) => {
  for (let attempt = 0; attempt < 2; attempt++) {
    const base = await woLoadOrders();          // throws → nothing written
    const prev = {}; base.forEach(o => { prev[o.id] = o; });
    let next = fn(base.slice());
    if (!next) return base;
    next = next.map(o => (UUID_RE.test(String(o.id || '')) ? o : { ...o, id: woNewId() }));
    const changed = next.filter(o => !prev[o.id] || JSON.stringify(o) !== JSON.stringify(prev[o.id]));
    if (!changed.length) return next;
    try {
      await rest(`${WO_TABLE}?on_conflict=id`, { method: 'POST', headers: { Prefer: 'resolution=merge-duplicates,return=minimal' }, body: JSON.stringify(changed.map(woToRow)) });
    } catch (e) {
      if (attempt === 0 && e.status === 409 && /fleet_work_orders_no_key|\bno\b/i.test(String(e.body || ''))) continue;
      throw e;
    }
    const ev = [];
    changed.forEach(o => {
      const p = prev[o.id]; const from = p ? (p.log || []).length : 0; const kind = eventKind(o, p);
      (o.log || []).slice(from).forEach(l => ev.push({ wo_id: o.id, at: l.at || new Date().toISOString(), by: l.by || null, by_name: l.byName || null, kind, note: String(l.text || '') }));
    });
    if (ev.length) { try { await rest('fleet_wo_events', { method: 'POST', headers: { Prefer: 'return=minimal' }, body: JSON.stringify(ev) }); } catch { /* audit trail only */ } }
    await touchStamp();
    return next;
  }
  throw new Error('Could not number the work order — try again.');
};

// Next WO-YY-#### over a list (the unique constraint on no is the real guard).
export const woNextNo = (list) => {
  const yy = String(new Date().getFullYear()).slice(-2); const re = new RegExp(`^WO-${yy}-(\\d+)$`); let max = 0;
  (list || []).forEach(x => { const m = String((x && x.no) || '').match(re); if (m) max = Math.max(max, Number(m[1])); });
  return `WO-${yy}-${String(max + 1).padStart(4, '0')}`;
};

// ── POs ────────────────────────────────────────────────────────────────────────
export const poAmount = (r) => {
  const inv = Array.isArray(r && r.invoices) ? r.invoices.filter(x => x && x.amount != null && !isNaN(Number(x.amount))) : [];
  if (inv.length) return inv.reduce((t, x) => t + Number(x.amount), 0);
  if (r && r.actual != null && r.actual !== '' && !isNaN(Number(r.actual))) return Number(r.actual);
  return 0;
};
export const poEstimate = (r) => (r && r.estimate != null && r.estimate !== '' && !isNaN(Number(r.estimate))) ? Number(r.estimate) : 0;
export const poUnitKey = (p) => (p && p.ducType === 'fleet-unit') ? unitKey(p.ducOrUnit || p.unit) : '';
// Every live PO log, each PO tagged with the key it lives in (so a recode writes back to the right list).
export const loadLivePos = async (sg) => {
  const out = []; let missed = false;
  await Promise.all(PO_LOG_CODES.map(async c => {
    let v = null; try { v = await sg('po-log-' + c); } catch { v = null; }
    if (Array.isArray(v)) v.forEach(p => { if (p && p.po != null) out.push({ ...p, _key: 'po-log-' + c }); });
    else if (v != null) missed = true;
  }));
  out.missed = missed;
  return out;
};
let _histOld = null;   // prior-year Bob history — read once a session (~4MB)
export const loadPoHistory = async (sg) => {
  const yr = new Date().getFullYear();
  if (!_histOld) {
    const keys = []; for (let y = 2021; y < yr; y++) keys.push(`po-hist-bm-${y}`);
    _histOld = Promise.all(keys.map(k => sg(k).then(v => ({ k, v })).catch(() => ({ k, v: null }))));
  }
  const [old, cur] = await Promise.all([_histOld, Promise.all([`po-hist-bm-${yr}`, 'po-hist-bm-undated'].map(k => sg(k).then(v => ({ k, v })).catch(() => ({ k, v: null }))))]);
  if (old.some(x => !Array.isArray(x.v))) _histOld = null;
  const out = [];
  old.concat(cur).forEach(({ k, v }) => { if (Array.isArray(v)) v.forEach(p => { if (p) out.push({ ...p, _key: k }); }); });
  return out;
};
// Recode one PO off a unit onto a job (Bob's "Move to job"). Re-reads the list it lives in, changes only that PO,
// and writes nothing if the read fails or the PO is not there.
export const poRecodeToJob = async (sg, ss, key, poNo, duc, reason, actorName) => {
  let list = null; try { list = await sg(key); } catch { list = null; }
  if (!Array.isArray(list)) throw new Error(`Could not read ${key} — nothing changed.`);
  const ix = list.findIndex(p => p && String(p.po) === String(poNo));
  if (ix < 0) throw new Error(`PO ${poNo} is no longer in ${key}.`);
  const p = list[ix]; const was = p.ducOrUnit || p.unit || '';
  const d = new Date(); const ds = `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  const note = `${ds} moved off unit ${was} to job ${duc} by ${actorName || 'Fleet'} — ${reason}`;
  const next = list.slice();
  next[ix] = { ...p, ducType: 'duc', ducOrUnit: duc, job: duc, unit: '', movedFromUnit: was, moveNote: note, fixNote: p.fixNote ? `${p.fixNote} | ${note}` : note };
  const ok = await ss(key, next);
  if (ok === false) throw new Error('Save failed — sign in again and retry.');
  return next[ix];
};

// ══════════════════════════════════════════════════════════════════════════════
//  Unit work history — every WO, PO, logged service, shop visit and DVIR on one unit, newest first.
//  Mounted in the rig file, the Master edit card and the shop dash unit look-up.
// ══════════════════════════════════════════════════════════════════════════════
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mdy = (s) => { const v = String(s || ''); const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : v; };
const sortKey = (s) => { const v = String(s || ''); if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v; const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/); if (!m) return ''; const y = m[3].length === 2 ? '20' + m[3] : m[3]; return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`; };

export const loadUnitHistory = async (sg, unit) => {
  const uk = unitKey(unit); if (!uk) return { items: [], total: 0 };
  const safe = async (k) => { try { return await sg(k); } catch { return null; } };
  const [wos, live, hist, svc, shop, ix] = await Promise.all([woLoadSafe(uk), loadLivePos(sg), loadPoHistory(sg), safe('fleet-service-log'), safe('fleet-shop-history'), safe('fleet-dvir-index')]);
  const items = []; const seenPo = new Set();
  wos.forEach(o => items.push({ kind: 'WO', at: o.doneAt || o.openedAt, id: 'wo' + o.id, title: `${o.no} · ${o.status === 'done' ? 'Closed' : o.status === 'moved' ? `Moved to job ${o.moveJob}` : o.status === 'void' ? 'Voided' : (WO_FORKS[o.fork] || 'Open')}`,
    sub: [o.status === 'done' ? (o.workDone || o.complaint) : o.complaint, o.doneByName ? `by ${o.doneByName}` : (o.assigneeName || o.assignee ? `assigned ${o.assigneeName || o.assignee}` : ''), o.hours ? `${o.hours}h` : '', (o.poNos || []).length ? `PO ${o.poNos.join(', ')}` : '',
      // #PM_KIT_FLOW_V1 — the PM kit's parts cost lands on the unit; the reading and any checklist follow-up show on the line
      o.pmKit && o.pmKit.lines && o.pmKit.lines.length ? `PM kit ${o.pmKit.kitName || ''} · ${o.pmKit.lines.length} parts` : '', o.pmKit && o.reading != null ? `reading ${Number(o.reading).toLocaleString()}` : '',
      o.pmKit && o.pmKit.result && o.pmKit.result.followUp ? `checklist → ${o.pmKit.result.followUp}` : ''].filter(Boolean).join(' · '),
    open: woLive(o), ...(o.pmKit && o.pmKit.total && o.pmKit.stage !== 'build' ? { amt: Number(o.pmKit.total) || 0 } : {}),
    ...(o.pmKit && o.pmKit.stage !== 'build' ? { wo: o } : {}) }));   // #PM_KIT_REPORT_V1 — tap the line: kit parts + checklist, print the service report
  live.concat(hist).forEach(p => {
    const moved = p.movedFromUnit && unitKey(p.movedFromUnit) === uk;
    if (poUnitKey(p) !== uk && !moved) return;
    const k = String(p.po); if (seenPo.has(k)) return; seenPo.add(k);
    const amt = moved ? 0 : poAmount(p);
    items.push({ kind: 'PO', at: p.date, id: 'po' + k, title: `PO ${p.po} · ${p.supplier || 'supplier n/a'}${moved ? ` · moved to job ${p.job || p.ducOrUnit}` : ''}`,
      sub: [p.description, p.poUser || p.issuedBy, !amt && poEstimate(p) ? `est ${money(poEstimate(p))}` : ''].filter(Boolean).join(' · '), amt });
  });
  ((svc && Array.isArray(svc.entries)) ? svc.entries : []).filter(e => e && e.uk === uk).forEach((e, i) => items.push({ kind: 'Service', at: e.at || e.dos, id: 'sv' + (e.id || i),
    title: `Service${e.serviced != null ? ` at ${Number(e.serviced).toLocaleString()}` : ''}`, sub: [e.work, e.shop ? `shop ${e.shop}` : (e.mechanic ? `by ${e.mechanic}` : '')].filter(Boolean).join(' · ') }));
  ((shop && Array.isArray(shop.records)) ? shop.records : []).filter(r => r && unitKey(r.unit) === uk).forEach((r, i) => items.push({ kind: 'Shop', at: r.dateOut || r.dateIn, id: 'sh' + i + r.dateIn,
    title: `In shop ${mdy(r.dateIn)}${r.dateOut ? ` → ${mdy(r.dateOut)}` : ''}${r.days ? ` · ${r.days} day${Number(r.days) === 1 ? '' : 's'}` : ''}`, sub: [r.diagnosis, r.mechanic ? `mechanic ${r.mechanic}` : ''].filter(Boolean).join(' · '), amt: Number(r.cost) || 0, labor: true }));
  const days = Object.keys((ix && ix.days) || {}).filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d)).sort().reverse().slice(0, 90);
  const dv = await Promise.all(days.map(d => safe('fleet-dvir-' + d)));
  dv.forEach(v => ((v && Array.isArray(v.items)) ? v.items : []).forEach(r => {
    if (!r) return;
    const mine = r.uk === uk || unitKey(r.unit) === uk || (r.defects || []).some(x => unitKey(x.unit) === uk);
    if (!mine) return;
    const defs = (r.defects || []).filter(x => unitKey(x.unit) === uk);
    items.push({ kind: 'DVIR', at: r.at || r.date, id: 'dv' + r.id, title: `DVIR ${r.type === 'post' ? 'post-trip' : 'pre-trip'} · ${r.driverName || r.driver || ''}${r.safe === false ? ' · DO NOT OPERATE' : ''}`,
      sub: defs.length ? defs.map(x => x.item).join(', ') + (r.repair && r.repair.status === 'corrected' ? ` · corrected by ${r.repair.byName || r.repair.by}` : ' · open') : 'no defects', open: defs.length > 0 && !(r.repair && r.repair.status) });
  }));
  items.sort((a, b) => sortKey(b.at).localeCompare(sortKey(a.at)));
  const total = items.reduce((t, x) => t + ((x.kind === 'PO' || x.kind === 'WO') ? (x.amt || 0) : 0), 0);   // #PM_KIT_FLOW_V1 — PM kit parts count toward the unit's spend
  return { items, total, poCount: items.filter(x => x.kind === 'PO' && x.amt).length, woFailed: !!wos.failed };
};

const KIND_CLR = (T) => ({ WO: T.accent, PO: T.gold, Service: T.good, Shop: T.blue || T.accent, DVIR: T.violet || T.muted });
// #PM_KIT_REPORT_V1 (2026-10-28) — Bill: "Can the kit order and check list ride along and attach to work history of unit?"
// A PM kit line in the unit's history opens to its parts (with cost) and the checklist as it was closed, plus the printable report.
function KitDetail({ o, T }) {
  const k = o.pmKit || {}; const r = k.result || {};
  const checks = (r.checks && r.checks.length) ? r.checks : (k.checklist || []).map(item => ({ item, ok: null }));
  return (
    <div style={{ margin: '6px 0 4px 66px', padding: '8px 10px', border: `1px solid ${T.line}`, borderRadius: 6, fontSize: 11.5 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 4 }}>
        <b style={{ color: T.text }}>{k.kitName || 'PM kit'}</b>
        <span style={{ color: T.muted }}>{r.doneAt ? `done ${mdy(String(r.doneAt).slice(0, 10))} by ${r.byName || ''}${r.onBehalf ? ` for ${r.onBehalf}` : ''}` : (o.status === 'done' ? 'closed' : 'open — not closed yet')}{r.reading != null ? ` · reading ${Number(r.reading).toLocaleString()}` : ''}</span>
        <button onClick={() => printKitReport(o)} style={{ marginLeft: 'auto', cursor: 'pointer', fontSize: 11, fontWeight: 800, background: 'transparent', border: `1px solid ${T.line}`, color: T.text, borderRadius: 6, padding: '2px 9px', fontFamily: 'inherit' }}>🖨 Service report</button>
      </div>
      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: .6, color: T.muted, textTransform: 'uppercase', marginTop: 4 }}>Kit parts · {money(k.total)}</div>
      {(k.lines || []).map((l, j) => (
        <div key={j} style={{ display: 'flex', gap: 8, padding: '1px 0' }}>
          <span style={{ fontFamily: 'Consolas, monospace', minWidth: 90, color: T.text }}>{l.part}</span><span style={{ flex: 1, minWidth: 0, color: T.muted }}>{l.desc}{l.vendor ? ` · ${l.vendor}` : ''}</span>
          <span style={{ color: T.text }}>×{l.qty}</span><span style={{ minWidth: 70, textAlign: 'right', color: T.text }}>{money((Number(l.qty) || 0) * (Number(l.each) || 0))}</span></div>))}
      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: .6, color: T.muted, textTransform: 'uppercase', marginTop: 6 }}>Checklist{r.followUp ? ` · follow-up ${r.followUp}` : ''}</div>
      {checks.map((c, j) => (
        <div key={j} style={{ display: 'flex', gap: 8, padding: '1px 0' }}>
          <span style={{ minWidth: 70, fontWeight: 900, color: c.ok === true ? T.good : c.ok === false ? T.bad : T.muted }}>{c.ok === true ? 'OK' : c.ok === false ? 'NEEDS ATTN' : '—'}</span>
          <span style={{ flex: 1, minWidth: 0, color: T.text }}>{c.item}{c.note ? <span style={{ color: T.muted }}> — {c.note}</span> : null}</span></div>))}
    </div>);
}
export function UnitWorkHistory({ sg, unit, theme, max }) {
  const T = { text: '#EDEEF0', muted: '#98A2AA', line: '#3A3F45', accent: '#FF7A1A', gold: '#E7B73B', good: '#34D399', bad: '#F87171', blue: '#38BDF8', violet: '#A78BFA', ...(theme || {}) };
  const [h, setH] = useState(null);
  const [kind, setKind] = useState('');
  const [all, setAll] = useState(false);
  const [n, setN] = useState(0);
  const [exp, setExp] = useState('');   // #PM_KIT_REPORT_V1 — the PM kit line opened under the history
  useEffect(() => {
    let dead = false; setH(null);
    (async () => { try { const r = await loadUnitHistory(sg, unit); if (!dead) setH(r); } catch { if (!dead) setH({ items: [], total: 0, err: true }); } })();
    return () => { dead = true; };
  }, [sg, unit, n]);
  useLiveKeys([WO_STAMP_KEY], () => setN(x => x + 1), _conn);
  const clr = KIND_CLR(T);
  const lim = max || 10;
  const list = h ? h.items.filter(x => !kind || x.kind === kind) : [];
  const shown = all ? list : list.slice(0, lim);
  const counts = {}; (h ? h.items : []).forEach(x => { counts[x.kind] = (counts[x.kind] || 0) + 1; });
  return (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: '10px 12px', marginTop: 10 }} data-unit-history="1">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1, textTransform: 'uppercase', color: T.accent }}>Work history · {unit}</div>
        {h && <span style={{ fontSize: 11.5, color: T.muted }}>{h.items.length} item{h.items.length === 1 ? '' : 's'} · POs + kits <b style={{ color: T.gold }}>{money(h.total)}</b>{h.poCount ? ` on ${h.poCount}` : ''}</span>}
        <span style={{ flex: 1 }} />
        {h && h.items.length > 0 && ['', 'WO', 'PO', 'Service', 'Shop', 'DVIR'].filter(k => !k || counts[k]).map(k => (
          <button key={k || 'all'} onClick={() => setKind(k)} style={{ cursor: 'pointer', fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 10, fontFamily: 'inherit',
            border: `1px solid ${kind === k ? (k ? clr[k] : T.accent) : T.line}`, background: kind === k ? `${k ? clr[k] : T.accent}22` : 'transparent', color: kind === k ? T.text : T.muted }}>{k || 'All'}{k ? ` ${counts[k]}` : ''}</button>
        ))}
      </div>
      {!h && <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>Reading work orders, POs, service, shop visits and DVIRs…</div>}
      {h && h.woFailed && <div style={{ fontSize: 11.5, color: T.bad, marginTop: 4 }}>Work orders could not be read just now — POs and the rest are below.</div>}
      {h && !h.items.length && <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>Nothing on file for {unit} yet.</div>}
      {shown.map((x, i) => (
        <div key={x.id + i} style={{ padding: '5px 0', borderTop: i ? `1px solid ${T.line}66` : 'none', fontSize: 12 }}>
          <div onClick={x.wo ? () => setExp(e => e === x.id ? '' : x.id) : undefined} title={x.wo ? 'Show the kit parts and checklist' : undefined}
            style={{ display: 'flex', gap: 8, alignItems: 'baseline', cursor: x.wo ? 'pointer' : 'default' }}>
            <span style={{ flex: '0 0 58px', fontWeight: 800, color: T.text, whiteSpace: 'nowrap' }}>{mdy(String(x.at || '').slice(0, 10)) || '—'}</span>
            <span style={{ flex: '0 0 50px', fontSize: 9.5, fontWeight: 900, letterSpacing: .6, color: clr[x.kind], textTransform: 'uppercase' }}>{x.wo ? 'PM' : x.kind}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: x.open ? T.gold : T.text, fontWeight: 700 }}>{x.title}{x.wo ? <span style={{ color: T.blue, fontWeight: 800 }}> {exp === x.id ? '▴' : '▾ kit + checklist'}</span> : null}</div>
              {x.sub ? <div style={{ color: T.muted, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.sub}</div> : null}
            </div>
            {x.amt ? <span style={{ fontWeight: 800, color: x.labor ? T.muted : T.text, whiteSpace: 'nowrap' }} title={x.labor ? 'Shop labor estimate (days × rate), not in the PO total' : ''}>{money(x.amt)}</span> : null}
          </div>
          {x.wo && exp === x.id && <KitDetail o={x.wo} T={T} />}
        </div>
      ))}
      {list.length > lim && <button onClick={() => setAll(a => !a)} style={{ cursor: 'pointer', marginTop: 6, fontSize: 11, fontWeight: 800, background: 'transparent', border: `1px solid ${T.line}`, color: T.muted, borderRadius: 6, padding: '3px 10px', fontFamily: 'inherit' }}>{all ? 'Show less' : `Show all ${list.length}`}</button>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
//  #PM_WO_REQUEST_V1 (2026-10-28) — photos on work orders. Bill: PMs send Fleet a work order with a picture.
//  Bucket wo-photos (private). Each person uploads into their own <auth uid>/ folder; anyone signed in can view
//  (Bob, the shop, the PM who asked). Photos ride on the order as photos[] in the extra jsonb — no new column.
// ════════════════════════════════════════════════════════════════════════════════
export const WO_PHOTO_BUCKET = 'wo-photos';
const _uidOf = (tok) => {
  try {
    const p = String(tok || '').split('.')[1] || '';
    const b = p.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (p.length % 4)) % 4);
    return (JSON.parse(atob(b)) || {}).sub || '';
  } catch { return ''; }
};
// Phone photos are 3–6 MB; a field connection chokes on that. Shrink to ~1800px JPEG when it helps.
const _shrinkPhoto = (file) => new Promise((res) => {
  try {
    if (!file || !/^image\/(jpeg|png|webp)$/i.test(file.type || '') || file.size < 700000) { res(file); return; }
    const url = URL.createObjectURL(file); const img = new Image();
    img.onload = () => {
      try {
        const s = Math.min(1, 1800 / Math.max(img.width, img.height));
        const c = document.createElement('canvas'); c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); URL.revokeObjectURL(url);
        c.toBlob(b => res(b && b.size < file.size ? new File([b], String(file.name || 'photo').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', 0.82);
      } catch { res(file); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); res(file); };
    img.src = url;
  } catch { res(file); }
});
export const woUploadPhoto = async (file0) => {
  if (!_conn.supaUrl) throw new Error('Not connected — reload the page.');
  if (file0 && file0.size > 20 * 1024 * 1024) throw new Error('Photo is over 20 MB.');
  const tok = await _tok();
  if (!tok) throw new Error('Not signed in — sign in again, then retry.');
  const uid = _uidOf(tok);
  if (!uid) throw new Error('Could not read your sign-in — sign in again, then retry.');
  const file = await _shrinkPhoto(file0);
  const ext = (String(file.type || '').split('/')[1] || String(file.name || '').split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '').replace('jpeg', 'jpg');
  const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const r = await fetch(`${_conn.supaUrl}/storage/v1/object/${WO_PHOTO_BUCKET}/${path}`, { method: 'POST',
    headers: { apikey: _conn.supaKey, Authorization: `Bearer ${tok}`, 'Content-Type': file.type || 'image/jpeg', 'x-upsert': 'true' }, body: file });
  if (!r.ok) { let t = ''; try { t = await r.text(); } catch { t = ''; } throw new Error(`Photo upload failed (${r.status}) ${t.slice(0, 120)}`); }
  return { path, name: file0.name || 'photo', type: file.type || 'image/jpeg', size: file.size || 0, at: new Date().toISOString() };
};
const _photoSign = {};
export const woPhotoUrl = async (path) => {
  if (!path || !_conn.supaUrl) return '';
  const hit = _photoSign[path];
  if (hit && hit.exp > Date.now() + 60000) return hit.url;
  try {
    const tok = await _tok();
    const r = await fetch(`${_conn.supaUrl}/storage/v1/object/sign/${WO_PHOTO_BUCKET}/${path}`, { method: 'POST',
      headers: { apikey: _conn.supaKey, Authorization: `Bearer ${tok || _conn.supaKey}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ expiresIn: 3600 }) });
    if (!r.ok) return '';
    const d = await r.json();
    const url = d && d.signedURL ? `${_conn.supaUrl}/storage/v1${d.signedURL}` : '';
    if (url) _photoSign[path] = { url, exp: Date.now() + 3600000 };
    return url;
  } catch { return ''; }
};
// Thumbnails; tap opens full size in a new tab. Used on the PM board, Bob's queue and the mechanic cards.
export function WoPhotoStrip({ photos, size }) {
  const list = Array.isArray(photos) ? photos.filter(p => p && p.path) : [];
  const sig = list.map(p => p.path).join('|');
  const [urls, setUrls] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => { const m = {}; for (const p of sig ? sig.split('|') : []) m[p] = await woPhotoUrl(p); if (alive) setUrls(m); })();
    return () => { alive = false; };
  }, [sig]);
  if (!list.length) return null;
  const s = size || 64;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0' }}>
      {list.map(p => urls[p.path]
        ? <a key={p.path} href={urls[p.path]} target="_blank" rel="noreferrer" title="Open full size" onClick={e => e.stopPropagation()}>
            <img src={urls[p.path]} alt="Work order" style={{ width: s, height: s, objectFit: 'cover', borderRadius: 6, border: '1px solid #2A3040', display: 'block' }} /></a>
        : <span key={p.path} style={{ width: s, height: s, borderRadius: 6, border: '1px dashed #2A3040', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#8C9AB8' }}>📷</span>)}
    </div>
  );
}
